import { useSequenceHintStore } from '@/components/sequences/sequenceHintStore'
import { useEffect, useRef } from 'react'
import { shouldSkipShortcut } from './keyboard'
import { isInOverlayLayer } from './overlayLayers'

const SEQUENCE_WINDOW_MS = 1200
// A normal tap releases the starter well under this — hiding the hint right on keyup made it vanish
// before anyone could read it. Long enough to glance at and move a finger to the second key, short of
// the full chord window so letting go still reads as "done looking," not stuck.
const HINT_RELEASE_GRACE_MS = 400
const BARE_MODIFIER_KEYS = new Set(['Shift', 'Control', 'Alt', 'Meta'])

export interface SequenceStarter {
	/** Lowercase single character, e.g. 'g'. Never also a standalone binding: see the RFC's conflict rule. */
	key: string
	/** Every second key this starter recognises, lowercase. Anything else cancels instead of completing. */
	continuations: ReadonlySet<string>
	/**
	 * Called with the lowercase second key once it matches `continuations`. The key is consumed either
	 * way: deciding there is nothing to do right now (a lab off, a role missing) is still a completion,
	 * never a fallthrough to whatever that key would otherwise bind to.
	 */
	onComplete: (key: string) => void
}

/**
 * Chord sequences (`G then D`): a starter key arms a 1,200ms window for a second key. Escape, a click, a
 * focus change, or a second key that continues no armed sequence all cancel it; a held key (`repeat`)
 * and a bare modifier do neither. Releasing the starter key doesn't cancel the chord itself — a fast
 * tap-tap still completes it — but it does start hiding the hint early, after a short grace period, see
 * `onKeyUp` below.
 */
export function useSequence(starters: readonly SequenceStarter[]): void {
	const pendingRef = useRef<{ starter: SequenceStarter; expiresAt: number } | null>(null)
	// Read fresh inside the listener without re-registering it on every render the caller's array churns.
	const startersRef = useRef(starters)
	startersRef.current = starters

	useEffect(() => {
		let expiryTimer: number | null = null
		let releaseGraceTimer: number | null = null

		const clearExpiryTimer = () => {
			if (expiryTimer !== null) {
				window.clearTimeout(expiryTimer)
				expiryTimer = null
			}
		}

		const clearReleaseGraceTimer = () => {
			if (releaseGraceTimer !== null) {
				window.clearTimeout(releaseGraceTimer)
				releaseGraceTimer = null
			}
		}

		// The physical starter key currently held down, armed and not yet released — distinct from
		// `pendingRef`, which stays armed for the full window regardless of this. Lets the hint disappear
		// the moment the key comes up, without cutting short the window a tap-tap chord still relies on.
		let heldStarterKey: string | null = null

		// The only writer of pendingRef, so the hint store can never drift from it: every arm, cancel or
		// consume goes through here. A real timer (on top of the lazy expiry check below, which stays as a
		// correctness net against background-tab throttling) is what lets the hint disappear on its own
		// after 1,200ms of silence, rather than only at the next keydown.
		const setPending = (value: { starter: SequenceStarter; expiresAt: number } | null) => {
			pendingRef.current = value
			if (!value) {
				heldStarterKey = null
			}
			// A real arm, cancel or completion always wins over a release's own delayed hide, pending or not.
			clearReleaseGraceTimer()
			useSequenceHintStore.getState().setArmedKey(value ? value.starter.key : null)
			clearExpiryTimer()
			if (value) {
				expiryTimer = window.setTimeout(() => setPending(null), SEQUENCE_WINDOW_MS)
			}
		}

		const cancel = () => {
			setPending(null)
		}

		const onKeyDown = (event: KeyboardEvent) => {
			if (event.repeat) {
				return
			}
			if (event.key === 'Escape') {
				cancel()
				return
			}
			if (BARE_MODIFIER_KEYS.has(event.key)) {
				return
			}
			if (event.metaKey || event.ctrlKey || event.altKey) {
				return
			}
			if (shouldSkipShortcut(event) || isInOverlayLayer(event.target)) {
				cancel()
				return
			}

			const key = event.key.toLowerCase()
			let pending = pendingRef.current
			// A pending sequence past its window is as good as none: this key starts fresh rather than
			// being swallowed as a continuation that can never match.
			if (pending && Date.now() > pending.expiresAt) {
				pending = null
			}
			setPending(null)
			if (pending) {
				if (pending.starter.continuations.has(key)) {
					event.preventDefault()
					event.stopPropagation()
					pending.starter.onComplete(key)
				}
				return
			}

			const starter = startersRef.current.find(s => s.key === key)
			if (starter) {
				event.preventDefault()
				event.stopPropagation()
				heldStarterKey = key
				setPending({ starter, expiresAt: Date.now() + SEQUENCE_WINDOW_MS })
			}
		}

		// Hint only, not cancellation: releasing the starter key starts hiding the hint, but the chord
		// itself keeps working for the rest of the window — a fast tap-tap shouldn't need the key held.
		// Hiding waits out HINT_RELEASE_GRACE_MS rather than firing on the spot: a normal tap releases the
		// key almost immediately, and hiding instantly there made the hint disappear before it was read.
		const onKeyUp = (event: KeyboardEvent) => {
			if (heldStarterKey === null || event.key.toLowerCase() !== heldStarterKey) {
				return
			}
			heldStarterKey = null
			clearReleaseGraceTimer()
			releaseGraceTimer = window.setTimeout(() => {
				releaseGraceTimer = null
				useSequenceHintStore.getState().setArmedKey(null)
			}, HINT_RELEASE_GRACE_MS)
		}

		// Capture phase: a continuation key must never reach a plain single-key binding on the same press.
		window.addEventListener('keydown', onKeyDown, true)
		window.addEventListener('keyup', onKeyUp, true)
		window.addEventListener('focusin', cancel, true)
		window.addEventListener('pointerdown', cancel, true)
		return () => {
			clearExpiryTimer()
			clearReleaseGraceTimer()
			window.removeEventListener('keydown', onKeyDown, true)
			window.removeEventListener('keyup', onKeyUp, true)
			window.removeEventListener('focusin', cancel, true)
			window.removeEventListener('pointerdown', cancel, true)
		}
	}, [])
}
